import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { ExternalLink, Rss } from "lucide-react";
import { useFirebaseUrl } from "../../hooks/useFirebaseUrl";
import { ProposalSlide } from "../../lib/postProposals";
import type { PendingTopicData } from "./TopicCard";
import { cn } from "../../lib/utils";

/**
 * Shared idea presentation: the mood board (onboarding first post and the
 * workspace idea detail view) and the compact workspace idea card pieces.
 */

// Fixed "pinned by hand" angles, so pieces don't jump between renders.
const TILTS = [-3, 2.5, -1.5, 3.5, -2.5, 1.5, -3.5, 2];

export type IdeaMoodBoardVariant = "moodboard" | "outline";
const angles = [-2, 1.5, -1, 2, -1.5, 1];
const tilt = (index: number): CSSProperties => ({
  transform: `rotate(${angles[index % angles.length]}deg)`,
});

type Pin = { style: React.CSSProperties; className: string };

export function ideaTitle(topic: PendingTopicData): string {
  return topic.content.category === "quote"
    ? topic.content.content?.quote || ""
    : topic.content.content?.title || "";
}

function firstCaptionLine(topic: PendingTopicData) {
  return topic.content.captionText?.split("\n").find(Boolean);
}

export function useMoodBoardPins() {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  let order = 0;
  return (): Pin => {
    const piece = order++;
    return {
      style: {
        transitionDelay: `${piece * 60}ms`,
        rotate: `${TILTS[piece % TILTS.length]}deg`,
      },
      className: cn(
        "transition-[opacity,transform,translate] duration-500 ease-out motion-reduce:transition-none",
        entered ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0",
      ),
    };
  };
}

/**
 * The idea itself — format, title, and the caption we wrote — as a pinned
 * index card. Standalone so other idea surfaces (the workspace idea cards) can
 * lead with the same card. Pass a mood-board pin to tilt and animate it in;
 * without one it renders flat.
 */
export function IdeaSummaryCard({
  topic,
  slideCount,
  pin,
  className,
}: {
  topic: PendingTopicData;
  slideCount: number;
  pin?: { style: CSSProperties; className: string };
  className?: string;
}) {
  const quote = topic.content.category === "quote";
  const title = ideaTitle(topic);
  const fullCaption = topic.content.captionText?.trim();
  // Paragraph gaps would spend the preview's few lines on blank space.
  const caption = fullCaption?.replace(/\n\s*\n+/g, "\n");
  const [captionOpen, setCaptionOpen] = useState(false);
  const [captionClamped, setCaptionClamped] = useState(false);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const captionId = useId();
  // Offer the full caption only when the preview actually cuts it off.
  useEffect(() => {
    const element = captionRef.current;
    if (!element || captionOpen) return;
    const measure = () => setCaptionClamped(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [caption, captionOpen]);
  return (
    <section
      aria-label="Title and caption"
      style={pin ? { ...pin.style, rotate: "-0.75deg" } : undefined}
      className={cn(
        "relative max-w-md bg-background px-5 pb-4 pt-5",
        pin ? cn(pin.className, "shadow-sm") : "rounded-xl border",
        className,
      )}
    >
      {pin && (
        <span
          aria-hidden="true"
          className="absolute -top-1.5 left-1/2 h-3.5 w-14 -translate-x-1/2 rotate-2 bg-amber-100/80"
        />
      )}
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {quote ? "Quote post" : `${slideCount}-slide carousel`}
      </p>
      {/* A quote is the whole post, so it's shown in full with its paragraphs. */}
      <h3 className={cn("mt-1.5", quote ? "whitespace-pre-line text-base font-medium italic leading-snug" : "line-clamp-3 onboarding-display text-lg sm:text-xl")}>
        {quote ? <>&ldquo;{title}&rdquo;</> : title}
      </h3>
      {caption && (
        <div className="mt-3 border-t border-dashed pt-3">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Caption
          </p>
          <p
            id={captionId}
            ref={captionRef}
            className={cn(
              "mt-1 whitespace-pre-line text-sm leading-relaxed text-foreground/80",
              !captionOpen && "line-clamp-4",
            )}
          >
            {captionOpen ? fullCaption : caption}
          </p>
          {(captionClamped || captionOpen) && (
            <button
              type="button"
              aria-expanded={captionOpen}
              aria-controls={captionId}
              onClick={() => setCaptionOpen((open) => !open)}
              className="mt-1.5 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {captionOpen ? "Show less" : "Show full caption"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function CollectedPhoto({
  path,
  alt,
  index,
  tilted,
  onShown,
}: {
  path: string;
  alt: string;
  index: number;
  tilted: boolean;
  /** Called once the photo has actually loaded. */
  onShown?: (path: string) => void;
}) {
  const { url } = useFirebaseUrl(path);
  const [failed, setFailed] = useState(false);
  if (!url || failed) return null;
  return (
    <li
      style={tilted ? tilt(index) : undefined}
      className={cn(
        "relative w-24 shrink-0 sm:w-28",
        tilted && "bg-background p-1.5 shadow-sm",
      )}
    >
      {tilted && (
        <span
          aria-hidden="true"
          className="absolute -top-1 left-1/3 h-3 w-10 -rotate-3 bg-amber-100/80"
        />
      )}
      <img
        src={url}
        alt={alt}
        onLoad={() => onShown?.(path)}
        onError={() => setFailed(true)}
        className={cn(
          "aspect-[4/3] w-full object-cover",
          !tilted && "rounded-md",
        )}
      />
    </li>
  );
}

/**
 * An idea's raw materials pinned up like a mood board: the idea card (title
 * and caption), photos from the article, and the slide outline. Deliberately
 * unassembled so it never reads as the finished post. Used by onboarding's
 * first post and the workspace idea detail view.
 */
export function IdeaMoodBoard({
  topic,
  slides,
  variant = "moodboard",
  bare = false,
}: {
  topic: PendingTopicData;
  slides: ProposalSlide[];
  variant?: IdeaMoodBoardVariant;
  /** Fill a container that already frames it (e.g. a dialog) instead of its own card. */
  bare?: boolean;
}) {
  const tilted = variant === "moodboard";
  const pin = useMoodBoardPins();
  const cardPin = pin();
  const quote = topic.content.category === "quote";
  // A quote post is its title; a lone note would only repeat it.
  const notes = quote ? [] : slides;
  const notePins = notes.map(() => pin());
  const photos = ideaPhotos(slides).slice(0, 6);
  // Photos load separately; the column appears once one actually shows.
  const [shownPhotos, setShownPhotos] = useState<ReadonlySet<string>>(new Set());
  const showPhoto = useCallback((path: string) => {
    setShownPhotos((current) => (current.has(path) ? current : new Set(current).add(path)));
  }, []);
  let source: URL | undefined;
  try {
    const url = new URL(topic.articleUrl);
    if (["https:", "http:"].includes(url.protocol)) source = url;
  } catch {
    /* Incomplete source metadata need not block reviewing the idea. */
  }
  return (
    <article
      aria-label={`Post idea: ${ideaTitle(topic)}`}
      className={cn(
        "min-w-0 p-5 sm:p-8",
        bare ? "min-h-full" : "rounded-3xl border shadow-sm",
        tilted ? "bg-muted/20" : "bg-background",
      )}
      style={
        tilted
          ? {
              backgroundImage:
                "radial-gradient(hsl(var(--muted-foreground) / 0.13) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <IdeaSummaryCard topic={topic} slideCount={slides.length} pin={tilted ? cardPin : undefined} className="sm:flex-1" />
        {photos.length > 0 && (
          <section
            aria-label="Photos we might use"
            hidden={shownPhotos.size === 0}
            className="sm:w-60 sm:shrink-0"
          >
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              Photos we might use
            </p>
            <ul className="flex flex-wrap items-center gap-3 py-2">
              {photos.map((photo, index) => (
                <CollectedPhoto
                  key={photo.path}
                  path={photo.path}
                  alt={photo.alt}
                  index={index}
                  tilted={tilted}
                  onShown={showPhoto}
                />
              ))}
            </ul>
          </section>
        )}
      </div>
      {notes.length > 0 && (
      <ol
        aria-label="Slide outline"
        className={cn(
          "mt-6",
          tilted
            ? "flex flex-wrap items-start gap-3"
            : "divide-y divide-border/70",
        )}
      >
        {notes.map((slide, index) => (
          <li
            key={index}
            style={tilted ? notePins[index].style : undefined}
            className={cn(
              tilted && notePins[index].className,
              tilted
                ? "w-[calc(50%-0.375rem)] rounded-sm bg-amber-50 p-2.5 text-amber-950 shadow-sm dark:bg-amber-950/60 dark:text-amber-50 sm:w-36"
                : "flex gap-4 py-4 first:pt-0",
            )}
          >
            <span
              className={cn(
                tilted
                  ? "block text-[10px] font-medium uppercase tracking-wide opacity-60"
                  : "w-5 shrink-0 pt-1 text-xs font-medium text-muted-foreground",
              )}
            >
              <span className={tilted ? "" : "sr-only"}>Slide </span>
              {index + 1}
            </span>
            <div className={tilted ? "mt-1" : "min-w-0"}>
              {/* Outline items, not page sections: the idea's title is the heading. */}
              <p className={tilted ? "line-clamp-3 text-xs font-medium leading-4" : "text-sm font-semibold leading-5"}>
                {slide.heading}
              </p>
            </div>
          </li>
        ))}
      </ol>
      )}
      {source && (
        <a
          href={source.href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex min-h-8 max-w-full items-center gap-1.5 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          <span className="truncate">
            From {topic.articleTitle?.trim() || source.hostname.replace(/^www\./, "")}
          </span>
          <ExternalLink aria-hidden="true" className="h-3 w-3 shrink-0" />
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      )}
    </article>
  );
}

/**
 * One set of photos for every view of an idea: only those assigned to its
 * slides (brand kit or article), in slide order — what the post will use.
 */
export function ideaPhotos(slides: ProposalSlide[]) {
  const photos: { path: string; alt: string }[] = [];
  const seen = new Set<string>();
  for (const slide of slides) {
    const path = slide.image?.storagePath;
    if (!path || seen.has(path)) continue;
    seen.add(path);
    photos.push({ path, alt: `For the slide “${slide.heading}”` });
  }
  return photos;
}

/**
 * Who the idea came from and when: the source's favicon and name, then the
 * article's own date, or the generation time once the idea is drafted.
 */
export function IdeaSourceHeader({
  topic,
  timeAgo,
  label,
  className,
}: {
  topic: PendingTopicData;
  /** Shown instead of the source's stored name. */
  label?: string;
  /** Relative time for the date shown, e.g. "1d ago". */
  timeAgo: string;
  className?: string;
}) {
  const [faviconFailed, setFaviconFailed] = useState(false);
  let hostname = "";
  try {
    hostname = new URL(topic.articleUrl).hostname;
  } catch {
    /* No favicon for an unparseable source URL. */
  }
  // Drafted ideas show when they were generated; others, when the source posted.
  const posted = topic.status === "generated" && topic.generatedAt
    ? "generated"
    : topic.articlePublishedAt ? "posted" : "found";
  return (
    <span className={cn("flex min-w-0 items-center gap-2 text-xs", className)}>
      {hostname && !faviconFailed ? (
        <img
          src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=32`}
          alt=""
          className="h-4 w-4 shrink-0 rounded-sm"
          referrerPolicy="no-referrer"
          onError={() => setFaviconFailed(true)}
        />
      ) : (
        <Rss aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-brand-ink" />
      )}
      <span className="truncate font-medium text-foreground/70">{label || topic.sourceName}</span>
      <span className="shrink-0 text-muted-foreground">
        · {posted} {timeAgo}
      </span>
    </span>
  );
}

/** The title and quoted caption lead the workspace idea preview. */
export function IdeaCardMaterials({ topic }: { topic: PendingTopicData }) {
  const caption = firstCaptionLine(topic);
  return (
    // Block layout, not a flex column: line-clamp has no effect on these
    // spans as flex items, so long titles and captions ran unclamped.
    <span className="block min-w-0 space-y-2.5">
      <span className="line-clamp-2 text-sm font-medium leading-5">
        {ideaTitle(topic)}
      </span>
      {caption && (
        <span className="line-clamp-3 border-l-2 border-border pl-2 text-xs leading-4 text-muted-foreground">
          {caption}
        </span>
      )}
    </span>
  );
}

/** Source links sit outside the preview button, followed by available photos. */
export function IdeaSourceLine({
  topic,
  slides,
  className,
}: {
  topic: PendingTopicData;
  slides: ProposalSlide[];
  className?: string;
}) {
  const photos = ideaPhotos(slides);
  const format = topic.content.category === "quote"
    ? "Quote post"
    : `${slides.length}-slide carousel`;
  return (
    <div className={cn("flex min-w-0 flex-col gap-2.5", className)}>
      <p className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground/80">
        <span className="shrink-0">{format} · from</span>
        {safeHttpUrl(topic.articleUrl) ? (
          <a
            href={topic.articleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-w-0 items-center gap-0.5 underline-offset-2 hover:text-foreground hover:underline"
          >
            <span className="truncate">{topic.articleTitle}</span>
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        ) : (
          <span className="truncate">{topic.articleTitle}</span>
        )}
      </p>
      {photos.length > 0 && (
        <span aria-label="Photos we might use" className="flex items-center">
          {photos.slice(0, 3).map((photo, photoIndex) => (
            <StackedPhoto
              key={photo.path}
              path={photo.path}
              alt={photo.alt}
              first={photoIndex === 0}
            />
          ))}
        </span>
      )}
    </div>
  );
}

function StackedPhoto({
  path,
  alt,
  first,
}: {
  path: string;
  alt: string;
  first: boolean;
}) {
  const { url } = useFirebaseUrl(path);
  return (
    <span
      className={cn(
        "block h-10 w-10 overflow-hidden rounded-md bg-muted ring-2 ring-background",
        !first && "-ml-2",
      )}
    >
      {url && (
        <img
          src={url}
          alt={alt}
          className="h-full w-full object-cover"
        />
      )}
    </span>
  );
}

function safeHttpUrl(url: string) {
  try {
    return ["http:", "https:"].includes(new URL(url).protocol);
  } catch {
    return false;
  }
}
